import { UserRole, OrganizationRole } from "@/types/enums";
import React from 'react'
import {
  Alert,
  AppBar,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
} from '@mui/material'
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  Schedule,
  AdminPanelSettings,
  Logout,
  Sports,
  CalendarToday,
  Close,
  KeyboardArrowDown,
  AccountCircle,
  Person,
  Settings,
  ArrowBack,
  Cancel as CancelIcon,
  GetApp,
  WifiOff,
  Code,
  Logout as LogoutIcon,
  MeetingRoom,
  WorkspacePremium,
} from '@mui/icons-material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useAppSelector } from '@/hooks/redux'
import { usePrefetchData } from '@/hooks/usePrefetchData'
import { usePWAInstall } from '@/hooks/usePWAInstall'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useSubscriptionTier } from '@/hooks/useSubscriptionTier'
import OrganizationSwitcher from './OrganizationSwitcher'
import { UserAvatar } from './common/UserAvatar'
import NotificationBell from './NotificationBell'
import { usePushSubscriptionSync } from '@/hooks/usePushSubscriptionSync'
import { usePageTracking } from '@/hooks/usePageTracking'
import { getUserPhotoUrl } from '@/utils/photoUtils'

const drawerWidth = 240

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [logoutDialogOpen, setLogoutDialogOpen] = React.useState(false)
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null)
  const [photoRefreshKey, setPhotoRefreshKey] = React.useState(Date.now())
  const [bottomNavValue, setBottomNavValue] = React.useState(0)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { token } = useAppSelector((state) => state.auth)

  // Prefetch data for all pages in background
  usePrefetchData()

  const { canInstall, install } = usePWAInstall('app')
  const { isOnline } = useOnlineStatus()
  const { isStarter } = useSubscriptionTier()

  // Keep push subscriptions in sync at the app level
  usePushSubscriptionSync()

  // Track page views for feature usage analytics
  usePageTracking()

  // Sync bottom navigation with current route
  React.useEffect(() => {
    const currentIndex = menuItems.findIndex(item => item.path === location.pathname)
    if (currentIndex !== -1) {
      setBottomNavValue(currentIndex)
    }
  }, [location.pathname])

  // Listen for user photo updates
  React.useEffect(() => {
    const handlePhotoUpdate = () => setPhotoRefreshKey(Date.now());
    window.addEventListener('userPhotoUpdated', handlePhotoUpdate);
    return () => window.removeEventListener('userPhotoUpdated', handlePhotoUpdate);
  }, []);

  // Compute user photo URL directly from user data — no API call needed
  // undefined = still loading auth, null = no photo configured
  const userPhotoUrl = React.useMemo(() => {
    if (!user || !token) return undefined;
    return getUserPhotoUrl(user, 'small', token);
  }, [user, token, photoRefreshKey]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true)
  }

  const handleLogoutConfirm = async () => {
    setLogoutDialogOpen(false)
    await logout()
  }

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false)
  }

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (userMenuAnchor) {
      setUserMenuAnchor(null);
      return;
    }
    setUserMenuAnchor(event.currentTarget);
  }

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null)
  }

  const handleUserMenuItemClick = (path: string) => {
    navigate(path)
    handleUserMenuClose()
  }

  const menuItems = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'Players', icon: <People />, path: '/players' },
    { text: 'Schedule', icon: <Schedule />, path: '/schedule' },
    { text: 'Leagues', icon: <Sports />, path: '/leagues' },
  ]

  // Check if current page is one of the main pages
  const mainPaths = ['/dashboard', '/players', '/schedule', '/leagues']
  const isMainPage = mainPaths.includes(location.pathname)

  // Track last main page
  const [lastMainPage, setLastMainPage] = React.useState('/dashboard')

  React.useEffect(() => {
    if (isMainPage) {
      setLastMainPage(location.pathname)
    }
  }, [location.pathname, isMainPage])

  const handleBackClick = () => {
    const searchParams = new URLSearchParams(location.search)

    // If on admin page with a league selected in catalog, go back to catalog list
    if (location.pathname === '/admin' && searchParams.get('tab') === 'catalog' && searchParams.get('league')) {
      navigate('/admin?tab=catalog')
      return
    }

    // If on admin/settings/player pages, go to last main page
    if (location.pathname === '/admin' || location.pathname === '/dev' || location.pathname === '/settings' || location.pathname === '/user-settings' || location.pathname.startsWith('/players/')) {
      navigate(lastMainPage)
    } else if (location.pathname.match(/^\/leagues\/([^/]+)\/teams\/[^/]+\/analysis$/)) {
      // If on team analysis page, go to league analysis page
      const leagueId = location.pathname.match(/^\/leagues\/([^/]+)\/teams/)?.[1]
      navigate(`/leagues/${leagueId}/analysis`)
    } else if (location.pathname.match(/^\/leagues\/[^/]+\/analysis$/)) {
      // If on league analysis page, go to leagues page
      navigate('/leagues')
    } else {
      navigate(-1) // Use browser history to go back
    }
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: { xs: 'block' }, '@media (min-width: 1024px)': { display: 'none' } }}>
        <Toolbar />
      </Box>
      
      {/* User Profile Section */}
      <Box sx={{ py: 2, px: 2, textAlign: 'center' }}>
        <Box 
          onClick={() => {
            navigate('/user-settings');
            setMobileOpen(false);
          }}
          sx={{ 
            cursor: 'pointer',
            display: 'inline-block',
            position: 'relative',
            '&:hover': { opacity: 0.8 }
          }}
        >
          <UserAvatar src={userPhotoUrl} size={64} sx={{ mx: 'auto', mb: 1 }} />
          <Box
            sx={{
              position: 'absolute',
              bottom: 4,
              right: -4,
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid',
              borderColor: 'background.paper',
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: 16, color: 'white', transform: 'rotate(-90deg)' }} />
          </Box>
        </Box>
        {user?.name && (
          <Typography variant="body2" fontWeight="medium">
            {user.name}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
          {user?.email}
        </Typography>
      </Box>
      <Divider />
      
      {/* Organization Switcher - Mobile Expandable */}
      <OrganizationSwitcher onClose={() => setMobileOpen(false)} isMobileDrawer compact={!mobileOpen} />
      <Divider />
      
      {/* Secondary navigation items only */}
      <Box sx={{ flexGrow: 1 }}>
        <List>
          {/* Org Settings (for org owners/admins) */}
          {(user?.role === UserRole.SUPER_ADMIN || 
            user?.availableOrganizations?.find(org => 
              org.organizationId === user?.currentOrganizationId && 
              (org.role === OrganizationRole.OWNER || org.role === OrganizationRole.ADMIN)
            )) && (
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === '/settings'}
                onClick={() => {
                  navigate('/settings');
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon><Settings /></ListItemIcon>
                <ListItemText primary="Org Settings" />
              </ListItemButton>
            </ListItem>
          )}

          {/* PLCN Admin Console (Super Admin only) — opens dedicated admin console */}
          {user?.role === UserRole.SUPER_ADMIN && (
            <ListItem disablePadding>
              <ListItemButton
                component="a"
                href={`${window.location.protocol}//admin.${window.location.host.replace(/^www\./, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setMobileOpen(false)}
              >
                <ListItemIcon><AdminPanelSettings /></ListItemIcon>
                <ListItemText primary="Admin Console" />
              </ListItemButton>
            </ListItem>
          )}

          {/* Dev Console (dev mode only) */}
          {import.meta.env.DEV && (
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === '/dev'}
                onClick={() => {
                  navigate('/dev');
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon><Code /></ListItemIcon>
                <ListItemText primary="Dev Console" />
              </ListItemButton>
            </ListItem>
          )}

          {/* Install App */}
          {canInstall && (
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => {
                  install();
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon><GetApp /></ListItemIcon>
                <ListItemText primary="Install App" />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </Box>
      
      {/* Logout button at bottom */}
      <Box>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                setMobileOpen(false);
                handleLogoutClick();
              }}
            >
              <ListItemIcon><Logout /></ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Box>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'white', 
          color: 'text.primary', 
          boxShadow: 1,
          width: '100%',
          zIndex: (theme: any) => theme.zIndex.modal + 3
        }}
      >
        <Toolbar>
          {/* Left side - Logo or Back Button */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center'
            }}
          >
            {isMainPage ? (
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                background: 'linear-gradient(135deg, #4caf50 0%, #2196f3 100%)' 
              }}>
                <CalendarToday />
              </Avatar>
            ) : (
              <>
                {/* Desktop: Show logo, Mobile: Show back arrow */}
                <Avatar sx={{ 
                  width: 40, 
                  height: 40, 
                  background: 'linear-gradient(135deg, #4caf50 0%, #2196f3 100%)',
                  display: { xs: 'none', lg: 'flex' }
                }}>
                  <CalendarToday />
                </Avatar>
                <Box 
                  sx={{ 
                    width: 40, 
                    height: 40, 
                    display: { xs: 'flex', lg: 'none' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onClick={handleBackClick}
                >
                  <ArrowBack sx={{ color: 'black' }} />
                </Box>
              </>
            )}
          </Box>

          {/* Center - Player Central Title */}
          <Box sx={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            flexGrow: 0,
            ml: 0,
            '@media (min-width: 1024px)': {
              position: 'static',
              left: 'auto',
              transform: 'none',
              ml: 2
            }
          }}>
            <Typography variant="h6" sx={{
              color: 'text.primary',
              userSelect: 'none'
            }}>
              Player Central
            </Typography>
          </Box>

          {/* Desktop/Tablet Navigation */}
          <Box sx={{ display: 'none', '@media (min-width: 1024px)': { display: 'flex' }, alignItems: 'center', gap: 2, ml: 3 }}>
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <Button
                  key={item.text}
                  color="inherit"
                  startIcon={item.icon}
                  onClick={() => navigate(item.path)}
                  sx={{
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    },
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      backgroundColor: isActive ? '#4caf50' : 'transparent',
                    },
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
          </Box>
          
          {/* Spacer to push user menu to the right */}
          <Box sx={{ flexGrow: 1 }} />
          
          {/* User Menu - Desktop */}
          <Box sx={{ display: 'none', '@media (min-width: 1024px)': { display: 'flex' }, alignItems: 'center', gap: 1.5 }}>
            <NotificationBell />
            <OrganizationSwitcher />
            <Box
              onClick={handleUserMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.7
                }
              }}
            >
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <UserAvatar
                  src={userPhotoUrl}
                  size={40}
                  sx={{
                    border: '2px solid',
                    borderColor: 'divider'
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Person sx={{ fontSize: 10, color: 'text.secondary' }} />
                </Box>
              </Box>
              <KeyboardArrowDown fontSize="small" />
            </Box>
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{ zIndex: (theme) => theme.zIndex.modal + 4 }}
              PaperProps={{
                elevation: 0,
                sx: {
                  width: 250,
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                  mt: 0.5,
                },
              }}
            >
              {/* Profile Section - Centered */}
              <Box sx={{ py: 2, px: 2, textAlign: 'center' }}>
                <UserAvatar src={userPhotoUrl} size={80} sx={{ mx: 'auto', mb: 1 }} />
                {user?.name && (
                  <Typography variant="body2" fontWeight="medium">
                    {user.name}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {user?.email}
                </Typography>
              </Box>
              <Divider />
              
              {/* Account Button */}
              <MenuItem onClick={() => handleUserMenuItemClick('/user-settings')} sx={{ mt: 1 }}>
                <ListItemIcon><AccountCircle /></ListItemIcon>
                <ListItemText>Account</ListItemText>
              </MenuItem>
              
              {/* Org Settings */}
              {(user?.role === UserRole.SUPER_ADMIN || 
                user?.availableOrganizations?.find(org => 
                  org.organizationId === user?.currentOrganizationId && 
                  (org.role === OrganizationRole.OWNER || org.role === OrganizationRole.ADMIN)
                )) && (
                <>
                  <Divider />
                  <MenuItem onClick={() => handleUserMenuItemClick('/settings')}>
                    <ListItemIcon><Settings /></ListItemIcon>
                    <ListItemText>Org Settings</ListItemText>
                  </MenuItem>
                </>
              )}
              
              {/* PLCN Admin Console (Super Admin only) — opens dedicated admin console */}
              {user?.role === UserRole.SUPER_ADMIN && (
                <MenuItem
                  component="a"
                  href={`${window.location.protocol}//admin.${window.location.host.replace(/^www\./, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleUserMenuClose}
                >
                  <ListItemIcon><AdminPanelSettings /></ListItemIcon>
                  <ListItemText>Admin Console</ListItemText>
                </MenuItem>
              )}

              {/* Dev Console (dev mode only) */}
              {import.meta.env.DEV && (
                <MenuItem onClick={() => handleUserMenuItemClick('/dev')}>
                  <ListItemIcon><Code /></ListItemIcon>
                  <ListItemText>Dev Console</ListItemText>
                </MenuItem>
              )}

              {/* Install App */}
              {canInstall && (
                <MenuItem onClick={() => { handleUserMenuClose(); install(); }}>
                  <ListItemIcon><GetApp /></ListItemIcon>
                  <ListItemText>Install App</ListItemText>
                </MenuItem>
              )}

              {isStarter && (user?.role === UserRole.SUPER_ADMIN ||
                user?.availableOrganizations?.find(org =>
                  org.organizationId === user?.currentOrganizationId &&
                  (org.role === OrganizationRole.OWNER || org.role === OrganizationRole.ADMIN)
                )) && (
                <MenuItem onClick={() => handleUserMenuItemClick('/settings?tab=billing')}>
                  <ListItemIcon><WorkspacePremium color="primary" /></ListItemIcon>
                  <ListItemText>Upgrade</ListItemText>
                </MenuItem>
              )}

              <Divider />
              <MenuItem onClick={() => { handleUserMenuClose(); handleLogoutClick(); }}>
                <ListItemIcon><Logout /></ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>

          {/* Mobile - notification bell and hamburger menu */}
          <Box sx={{ flexGrow: 1, display: { xs: 'block', '@media (min-width: 1024px)': { display: 'none' } } }} />
          <Box sx={{
            display: { xs: 'flex' },
            '@media (min-width: 1024px)': { display: 'none' },
            alignItems: 'center',
            gap: 0.5
          }}>
            <NotificationBell size="small" />
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="end"
              onClick={handleDrawerToggle}
            >
              {mobileOpen ? <Close /> : <MenuIcon />}
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        anchor="right"
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block' },
          '@media (min-width: 1024px)': { display: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          zIndex: (theme) => theme.zIndex.modal + 2,
        }}
      >
        {drawer}
      </Drawer>

      {/* Logout confirmation dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
      >
        <DialogTitle id="logout-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MeetingRoom />
            Confirm Logout
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleLogoutCancel} startIcon={<CancelIcon />}>
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} color="primary" variant="contained" startIcon={<LogoutIcon />}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 1,
          width: '100%',
          position: 'relative',
          paddingBottom: { 
            xs: location.pathname === '/settings' || location.pathname === '/admin' || location.pathname === '/dev' || location.pathname === '/user-settings' || location.pathname.startsWith('/players/') ? '16px' : '80px',
            lg: '16px' 
          }, // No bottom padding on settings/admin/user-settings/player profile pages
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '120px',
            background: 'linear-gradient(to bottom, #e8f5e8 0%, white 100%)',
            zIndex: -1
          }
        }}
      >
        <Toolbar />
        <Collapse in={!isOnline}>
          <Alert severity="warning" icon={<WifiOff fontSize="inherit" />} sx={{ mb: 1 }}>
            You're offline. Showing cached data.
          </Alert>
        </Collapse>
        {children}
      </Box>

      {/* Mobile Bottom Navigation - only show when hamburger menu is used (below 1024px) */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: location.pathname === '/settings' || location.pathname === '/admin' || location.pathname === '/dev' || location.pathname === '/user-settings' || location.pathname.startsWith('/players/') ? 'none' : 'block',
          '@media (min-width: 1024px)': {
            display: 'none'
          },
          zIndex: (theme: any) => theme.zIndex.appBar,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
        elevation={3}
      >
        <BottomNavigation
          value={bottomNavValue}
          onChange={(_, newValue) => {
            setBottomNavValue(newValue)
            navigate(menuItems[newValue].path)
          }}
          showLabels
        >
          {menuItems.map((item) => (
            <BottomNavigationAction
              key={item.text}
              label={item.text}
              icon={item.icon}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  )
}

export default Layout
